import { getCustomerId } from 'src/api';
import { SubmitBookingFormData } from 'src/components/checkout/SectionCheckout';
import { Address, BasicAddress } from 'src/entities/basic-types/Address';
import SelectedProduct from 'src/entities/cart/request/SelectedProduct';
import { VoucherSelection, withoutFreePiecesOf } from 'src/entities/cart/request/VoucherSelection';
import OrderProduct from 'src/entities/cart/response/OrderProduct';
import ShoppingCart from 'src/entities/cart/response/ShoppingCart';
import CustomerOrder from 'src/entities/customer/CustomerOrder';
import { MyOffer } from 'src/entities/offer/MyOffer';
import CustomFieldOrderProduct from '../response/CustomFieldOrderProduct';

/** The technical article every „Leerartikel" stands on (ordering V152). */
const BLANK_ARTICLE_NUMBER = 1619111;

export default class CartRequestDTO {
    orderProductList: SelectedProduct[];
    currencyIso: string | undefined;
    paymentMethodIdentifier?: string;
    billingAddress?: Address | BasicAddress;
    deliveryAddress?: Address | BasicAddress;
    extra?: Record<string, string>;
    fromLandingPage?: boolean;
    fromLandingPageId?: string;
    useCredit?: boolean;
    publicId?: string;
    /** WS-3 (Runde 2): the chosen outbound shipping provider — set post-construction. */
    shippingSupplierId?: number | null;
    /** R28-2: the SEPA mandate chosen at the checkout (several mandates). */
    sepaMandateId?: string;
    customerId?: number;
    selectedSourceDepot?: string;
    selectedSendMailToCustomer?: boolean;
    note?: string;
    skipDefaultVendorOverride?: boolean;
    voucherCodes?: string[];
    /** Live-Runde 10: the product picked for a product group voucher, by voucher code. */
    voucherTargets?: Record<string, number>;
    /** Live-Runde 10: catalogue codes the customer unchecked — no Rabatt-Gutschein is proposed instead. */
    declinedVoucherCodes?: string[];
    /** Order properties the shop may set — R26-1: selfRebate "true" deducts the buying partner's own commission. */
    additionalProperties?: Record<string, string>;

    constructor(
        orderProductList: SelectedProduct[],
        paymentMethodIdentifier?: string,
        currencyIso?: string,
        billingAddress?: Address | BasicAddress,
        deliveryAddress?: Address | BasicAddress,
        extra?: Record<string, string>,
        fromLandingPage?: boolean,
        fromLandingPageId?: string,
        useCredit?: boolean,
        publicId?: string,
        customerId?: number,
        selectedSourceDepot?: string,
        selectedSendMailToCustomer?: boolean,
        note?: string,
        skipDefaultVendorOverride?: boolean,
        voucherCodes?: string[]
    ) {
        this.orderProductList = orderProductList;
        this.paymentMethodIdentifier = paymentMethodIdentifier;
        this.currencyIso = currencyIso || 'EUR';
        this.billingAddress = billingAddress;
        this.deliveryAddress = deliveryAddress;
        this.extra = extra;
        this.fromLandingPage = fromLandingPage;
        this.fromLandingPageId = fromLandingPageId;
        this.useCredit = useCredit;
        this.publicId = publicId;
        this.customerId = customerId;
        this.selectedSourceDepot = selectedSourceDepot;
        this.selectedSendMailToCustomer = selectedSendMailToCustomer;
        this.note = note;
        this.skipDefaultVendorOverride = skipDefaultVendorOverride;
        this.voucherCodes = voucherCodes;
    }

    /**
     * Resolves the voucherCodes (0 or 1 entries, D-U5-1) for a cart request.
     * `explicitCode === undefined` means "no explicit choice" → carry the voucher already applied to the cart;
     * an empty string is an explicit deselect ("Kein Gutschein") → no voucher is sent.
     */
    private withSepaMandate(sepaMandateId: string | null | undefined): CartRequestDTO {
        this.sepaMandateId = sepaMandateId || undefined;
        return this;
    }

    private withVoucherChoice(selection: VoucherSelection): CartRequestDTO {
        this.declinedVoucherCodes = selection.declined.length > 0 ? [...selection.declined] : undefined;
        this.voucherTargets = Object.keys(selection.targets).length > 0 ? { ...selection.targets } : undefined;
        return this;
    }

    private withShipping(shippingSupplierId: number | null | undefined): CartRequestDTO {
        this.shippingSupplierId = shippingSupplierId ?? undefined;
        return this;
    }

    /** R26-1: the checkout choice travels as the selfRebate order property; unchanged carts carry the echoed choice, else the present rebate line. */
    private withSelfRebate(selfRebate: boolean | undefined): CartRequestDTO {
        if (selfRebate != null) {
            this.additionalProperties = { ...(this.additionalProperties ?? {}), selfRebate: String(selfRebate) };
        }
        return this;
    }

    /**
     * R45: a calc carries the customer's own carrier choice only — the calc resolves its preselection
     * anew from the current addresses, and another delivery address drops the earlier choice.
     */
    private static shippingChoiceForCalc(shoppingCart: ShoppingCart, data?: SubmitBookingFormData) {
        if (data?.shippingSupplierId != null) return data.shippingSupplierId;
        const deliveryChanged =
            data?.deliveryAddress?.id != null && data.deliveryAddress.id !== shoppingCart.deliveryAddress?.id;
        return shoppingCart.shippingSupplierPreselected || deliveryChanged
            ? undefined
            : shoppingCart.shippingSupplierId;
    }

    /** The legacy voucher (0 or 1, D-U5-1) plus the checked catalogue vouchers (Live-Runde 10). */
    private static voucherCodesFrom(
        shoppingCart: ShoppingCart,
        explicitCode?: string,
        selection: VoucherSelection = shoppingCart.voucherSelection()
    ): string[] | undefined {
        const code = explicitCode !== undefined ? explicitCode : shoppingCart.appliedVoucher?.code;
        const codes = [...(code ? [code] : []), ...selection.codes.filter(checked => checked !== code)];
        return codes.length > 0 ? codes : undefined;
    }

    static fromShoppingCart(
        shoppingCart: ShoppingCart,
        data?: SubmitBookingFormData,
        fromLandingPage?: boolean,
        fromLandingPageId?: string,
        voucherSelection: VoucherSelection = shoppingCart.voucherSelection()
    ): CartRequestDTO {
        const shoppingCartProducts = shoppingCart
            .getFlatProductList()
            .map((product: OrderProduct) => SelectedProduct.fromOrderProduct(product));

        // create new CartRequestDTO and filter out products with zero quantity
        return new CartRequestDTO(
            shoppingCartProducts.filter(product => product.quantity > 0),
            data?.paymentMethodIdentifier ?? shoppingCart.paymentMethodIdentifier,
            shoppingCart.currencyIso,
            data?.billingAddress?.id ? data.billingAddress : undefined,
            data?.deliveryAddress?.id ? data.deliveryAddress : undefined,
            data?.extra,
            fromLandingPage ?? shoppingCart.fromLandingPage,
            fromLandingPageId ?? shoppingCart.fromLandingPageId,
            data?.useCredit,
            shoppingCart.publicId,
            this.checkCustomerId(data?.customerId ?? shoppingCart.customerId),
            data?.selectedSourceDepot ?? shoppingCart.selectedSourceDepot,
            data?.selectedSendMailToCustomer ?? shoppingCart.selectedSendMailToCustomer,
            undefined,
            data?.skipDefaultVendorOverride ?? shoppingCart.skipDefaultVendorOverride,
            this.voucherCodesFrom(shoppingCart, data?.voucherCode, voucherSelection)
        )
            .withVoucherChoice(voucherSelection)
            .withShipping(this.shippingChoiceForCalc(shoppingCart, data))
            .withSepaMandate(data?.sepaMandateId)
            .withSelfRebate(
                data?.selfRebate ?? shoppingCart.selfRebateChoice ?? (shoppingCart.selfRebate || undefined)
            );
    }

    /**
     * Creates a CartRequestDTO from the given shopping cart and additional products.
     */
    static fromShoppingCartAndNew(shoppingCart: ShoppingCart, newProducts: any[]): CartRequestDTO {
        // extract all existing products within our shopping cart state and flatten them
        const shoppingCartProducts = shoppingCart
            .getFlatProductList()
            .map((product: OrderProduct) => SelectedProduct.fromOrderProduct(product));

        SelectedProduct.fromProductsSelectForm(newProducts).forEach(selectProduct => {
            const flattened = selectProduct.flattenNestedChosenVariants();

            // get index of existing product in shopping cart
            const index = shoppingCartProducts.findIndex(p => p.equals(flattened));
            // if product not found -> add it
            if (index === -1) shoppingCartProducts.push(flattened);
            // else increase quantity of existing product
            else {
                shoppingCartProducts[index].quantity += flattened.quantity;
            }
        });

        // create new CartRequestDTO and filter out products with zero quantity
        return new CartRequestDTO(
            shoppingCartProducts.filter(product => product.quantity > 0),
            shoppingCart.paymentMethodIdentifier,
            shoppingCart.currencyIso,
            undefined,
            undefined,
            undefined,
            shoppingCart.fromLandingPage,
            shoppingCart.fromLandingPageId,
            undefined,
            shoppingCart.publicId,
            this.checkCustomerId(shoppingCart.customerId),
            shoppingCart.selectedSourceDepot,
            shoppingCart.selectedSendMailToCustomer,
            undefined,
            shoppingCart.skipDefaultVendorOverride,
            this.voucherCodesFrom(shoppingCart)
        )
            .withVoucherChoice(shoppingCart.voucherSelection())
            .withSelfRebate(shoppingCart.selfRebateChoice ?? (shoppingCart.selfRebate || undefined));
    }

    /**
     * Creates a CartRequestDTO from the given shopping cart without the given item
     */
    static fromShoppingCartWithoutItem(shoppingCart: ShoppingCart, selectedProduct: SelectedProduct): CartRequestDTO {
        const shoppingCartProducts = shoppingCart
            .getFlatProductList()
            .map((product: OrderProduct) => SelectedProduct.fromOrderProduct(product));

        const indexToRemove = shoppingCartProducts.findIndex((p: SelectedProduct) => p.equals(selectedProduct));
        const voucherSelection = withoutFreePiecesOf(
            shoppingCart.voucherSelection(),
            shoppingCart.vouchers,
            selectedProduct.ffProductNumber
        );

        // create new CartRequestDTO and filter out the product we want to remove
        return new CartRequestDTO(
            shoppingCartProducts.filter((product, index) => index !== indexToRemove),
            shoppingCart.paymentMethodIdentifier,
            shoppingCart.currencyIso,
            undefined,
            undefined,
            undefined,
            shoppingCart.fromLandingPage,
            shoppingCart.fromLandingPageId,
            undefined,
            shoppingCart.publicId,
            this.checkCustomerId(shoppingCart.customerId),
            shoppingCart.selectedSourceDepot,
            shoppingCart.selectedSendMailToCustomer,
            undefined,
            shoppingCart.skipDefaultVendorOverride,
            this.voucherCodesFrom(shoppingCart, undefined, voucherSelection)
        )
            .withVoucherChoice(voucherSelection)
            .withSelfRebate(shoppingCart.selfRebateChoice ?? (shoppingCart.selfRebate || undefined));
    }

    /**
     * Creates a CartRequestDTOAdmin from the given shopping cart without the given items
     */
    static fromShoppingCartWithoutItems(shoppingCart: ShoppingCart): CartRequestDTO {
        // create new CartRequestDTOAdmin without items
        shoppingCart = ShoppingCart.emptyCart();

        const shoppingCartProducts = shoppingCart
            .getFlatProductList()
            .map((product: OrderProduct) => SelectedProduct.fromOrderProduct(product));

        return new CartRequestDTO(shoppingCartProducts, shoppingCart.paymentMethodIdentifier, shoppingCart.currencyIso);
    }

    /**
     * Creates a CartRequestDTO from the given shopping cart and updates the quantity of the given product
     */
    static fromShoppingCartWithProductQuantityChange(
        shoppingCart: ShoppingCart,
        selectedProduct: SelectedProduct,
        quantity: number
    ): CartRequestDTO {
        const shoppingCartProducts = shoppingCart
            .getFlatProductList()
            .map((product: OrderProduct) => SelectedProduct.fromOrderProduct(product));

        const index = shoppingCartProducts.findIndex((p: SelectedProduct) => p.equals(selectedProduct));

        // found index of product to change quantity
        if (index !== -1) shoppingCartProducts[index].quantity = quantity;
        const remaining = shoppingCartProducts.filter(product => product.quantity > 0);
        // Live-Runde 10: quantity 0 deletes the product — and its free pieces, or ordering would add it again
        const voucherSelection = remaining.some(product => product.ffProductNumber === selectedProduct.ffProductNumber)
            ? shoppingCart.voucherSelection()
            : withoutFreePiecesOf(
                  shoppingCart.voucherSelection(),
                  shoppingCart.vouchers,
                  selectedProduct.ffProductNumber
              );

        // create new CartRequestDTO and filter out products with zero quantity
        return new CartRequestDTO(
            remaining,
            shoppingCart.paymentMethodIdentifier,
            shoppingCart.currencyIso,
            undefined,
            undefined,
            undefined,
            shoppingCart.fromLandingPage,
            shoppingCart.fromLandingPageId,
            undefined,
            shoppingCart.publicId,
            this.checkCustomerId(shoppingCart.customerId),
            shoppingCart.selectedSourceDepot,
            shoppingCart.selectedSendMailToCustomer,
            undefined,
            shoppingCart.skipDefaultVendorOverride,
            this.voucherCodesFrom(shoppingCart, undefined, voucherSelection)
        )
            .withVoucherChoice(voucherSelection)
            .withSelfRebate(shoppingCart.selfRebateChoice ?? (shoppingCart.selfRebate || undefined));
    }

    /**
     * Creates a CartRequestDTO from the given shopping cart and updates the customFields of the given product
     */
    static fromShoppingCartWithProductCustomFieldValueChange(
        shoppingCart: ShoppingCart,
        selectedProduct: SelectedProduct,
        fieldName: string,
        value: string
    ): CartRequestDTO {
        const shoppingCartProducts = shoppingCart
            .getFlatProductList()
            .map((product: OrderProduct) => SelectedProduct.fromOrderProduct(product));

        const index = shoppingCartProducts.findIndex((p: SelectedProduct) => p.equals(selectedProduct));

        // found index of product to change custom field
        if (index !== -1) {
            const customFields = shoppingCartProducts[index].customFields;
            if (customFields) {
                const customFieldIndex = customFields.findIndex(field => field.fieldName === fieldName);
                if (customFieldIndex !== -1) {
                    const customField = customFields[customFieldIndex];
                    shoppingCartProducts[index].customFields![customFieldIndex] = new CustomFieldOrderProduct({
                        ...customField,
                        value,
                    });
                }
            }
        }

        // create new CartRequestDTO and filter out products with zero custom field
        return new CartRequestDTO(
            shoppingCartProducts,
            shoppingCart.paymentMethodIdentifier,
            shoppingCart.currencyIso,
            undefined,
            undefined,
            undefined,
            shoppingCart.fromLandingPage,
            shoppingCart.fromLandingPageId,
            undefined,
            shoppingCart.publicId,
            this.checkCustomerId(shoppingCart.customerId),
            shoppingCart.selectedSourceDepot,
            shoppingCart.selectedSendMailToCustomer,
            undefined,
            shoppingCart.skipDefaultVendorOverride,
            this.voucherCodesFrom(shoppingCart)
        )
            .withVoucherChoice(shoppingCart.voucherSelection())
            .withSelfRebate(shoppingCart.selfRebateChoice ?? (shoppingCart.selfRebate || undefined));
    }

    /**
     * Creates a CartRequestDTO from the given shopping cart and updates the quantity of the given product
     */
    static FromShoppingCartWithSubmitBookingFormData(
        shoppingCart: ShoppingCart,
        data: SubmitBookingFormData
    ): CartRequestDTO {
        const shoppingCartProducts = shoppingCart
            .getFlatProductList()
            .map((product: OrderProduct) => SelectedProduct.fromOrderProduct(product));

        // create new CartRequestDTO including to submit booking form data
        return new CartRequestDTO(
            shoppingCartProducts.filter(product => product.quantity > 0),
            data.paymentMethodIdentifier,
            shoppingCart.currencyIso,
            data.billingAddress,
            data.deliveryAddress,
            data.extra,
            shoppingCart.fromLandingPage,
            shoppingCart.fromLandingPageId,
            data.useCredit,
            shoppingCart.publicId,
            this.checkCustomerId(shoppingCart.customerId),
            shoppingCart.selectedSourceDepot,
            shoppingCart.selectedSendMailToCustomer,
            data.note,
            shoppingCart.skipDefaultVendorOverride,
            this.voucherCodesFrom(shoppingCart, data.voucherCode)
        )
            .withVoucherChoice(shoppingCart.voucherSelection())
            .withShipping(data.shippingSupplierId ?? shoppingCart.shippingSupplierId)
            .withSelfRebate(data.selfRebate ?? shoppingCart.selfRebateChoice ?? (shoppingCart.selfRebate || undefined));
    }

    // fromExistingOrder / fromExistingOffer intentionally send no voucherCodes:
    // order-again and offer acceptance start without a voucher; the checkout preselection applies one if eligible.
    static fromExistingOrder(order: CustomerOrder): CartRequestDTO {
        return new CartRequestDTO(
            order.orderProductList
                // Live-Runde 14 item 13: a „Leerartikel" belongs to its one order only
                .filter(product => Number(product.ffProductNumber) !== BLANK_ARTICLE_NUMBER)
                .map(product => SelectedProduct.fromCustomerOrderProduct(product)),
            order.currencyIso
        );
    }

    static fromExistingOffer(offer: MyOffer): CartRequestDTO {
        return new CartRequestDTO(
            offer.orderProductList,
            offer.paymentMethodIdentifier,
            offer.currencyIso,
            offer.billingAddress,
            offer.deliveryAddress,
            offer.additionalProperties,
            false,
            undefined,
            false,
            offer.publicId,
            offer.customerId
        );
    }

    static checkCustomerId = (customerId?: number) => {
        const urlCustomerId = getCustomerId() === 'current' ? customerId : Number(getCustomerId());
        if (customerId !== urlCustomerId) {
            return urlCustomerId;
        }
        return customerId;
    };
}
